import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api'
import type {
  DatosEntrada,
  FiltroDestino,
  GrillaRespuesta,
  PublicacionDetalle,
  PublicacionEntrada,
} from '@/lib/schemas/publicaciones'

/** Filtros de la grilla que viajan a la API y se guardan en la URL. */
export interface FiltrosGrilla {
  mes: string | null
  pilarIds: string[]
  /** Todo, sólo feed o sólo historias (DEC-21). */
  destino: FiltroDestino
  mostrarDeBaja: boolean
  /** Cuántas piezas pedir: crece de a 24 con "Mostrar más". */
  limite: number
  /**
   * Rango de días del calendario, "AAAA-MM-DD..AAAA-MM-DD". La cuadrícula
   * incluye días de los meses vecinos, así que no alcanza con `mes`.
   */
  rango?: string | null
}

const CLAVES = {
  grilla: (proyectoId: string, f: FiltrosGrilla) =>
    [
      'grilla',
      proyectoId,
      f.mes,
      f.rango ?? null,
      [...f.pilarIds].sort().join(','),
      f.destino,
      f.mostrarDeBaja,
      f.limite,
    ] as const,
  publicacion: (id: string) => ['publicacion', id] as const,
  contadores: ['contadores'] as const,
}

const jsonInit = (metodo: 'POST' | 'PUT', cuerpo?: unknown): RequestInit => ({
  method: metodo,
  ...(cuerpo === undefined
    ? {}
    : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cuerpo) }),
})

export function useGrilla(proyectoId: string, f: FiltrosGrilla) {
  const api = useApi()
  const params = new URLSearchParams({ proyecto: proyectoId, limite: String(f.limite) })
  if (f.mes) params.set('mes', f.mes)
  if (f.rango) params.set('rango', f.rango)
  if (f.pilarIds.length > 0) params.set('pilar', f.pilarIds.join(','))
  if (f.destino !== 'todo') params.set('destino', f.destino)
  if (f.mostrarDeBaja) params.set('baja', '1')
  return useQuery({
    queryKey: CLAVES.grilla(proyectoId, f),
    queryFn: () => api<GrillaRespuesta>(`/api/publicaciones/grilla?${params.toString()}`),
    enabled: Boolean(proyectoId),
    placeholderData: (previa) => previa,
  })
}

/** "N para revisar" de cada proyecto, para el selector del encabezado (§7.4). */
export function useContadores(habilitado: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: CLAVES.contadores,
    queryFn: () => api<Record<string, number>>('/api/publicaciones/contadores'),
    enabled: habilitado,
    staleTime: 30_000,
  })
}

export function usePublicacion(id: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: CLAVES.publicacion(id ?? ''),
    queryFn: () => api<PublicacionDetalle>(`/api/publicaciones/${id ?? ''}`),
    enabled: Boolean(id),
  })
}

function useInvalidar() {
  const qc = useQueryClient()
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['grilla'] }),
      qc.invalidateQueries({ queryKey: ['publicacion'] }),
      // El panel (apiRevision) muestra estado, fecha y acciones: sin esto,
      // abrirlo después de enviar a revisión mostraba el estado anterior.
      qc.invalidateQueries({ queryKey: ['panel'] }),
      qc.invalidateQueries({ queryKey: CLAVES.contadores }),
    ])
  }
}

export function useGuardarPublicacion() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({ id, datos }: { id: string | null; datos: PublicacionEntrada }) =>
      id
        ? api<PublicacionDetalle>(`/api/publicaciones/${id}`, jsonInit('PUT', datos))
        : api<PublicacionDetalle>('/api/publicaciones', jsonInit('POST', datos)),
    onSuccess: invalidar,
  })
}

export function useGuardarDatos() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: DatosEntrada }) =>
      api<PublicacionDetalle>(`/api/publicaciones/${id}/datos`, jsonInit('PUT', datos)),
    onSuccess: invalidar,
  })
}

export function useEnviarARevision() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ enviadas: number }>(`/api/publicaciones/${id}/enviar`, jsonInit('POST')),
    onSuccess: invalidar,
  })
}

export function useEnviarBorradores() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (proyectoId: string) =>
      api<{ enviadas: number }>(
        '/api/publicaciones/enviar-borradores',
        jsonInit('POST', { proyectoId }),
      ),
    onSuccess: invalidar,
  })
}

/**
 * Reprogramar mueve la pieza en pantalla antes de que conteste el servidor:
 * en el calendario, el usuario ya la soltó en el día nuevo, y verla volver un
 * segundo a su día anterior parecía un error (revisión OO, 21/09/2026). Si el
 * guardado falla, todo vuelve a como estaba.
 */
export function useReprogramar() {
  const api = useApi()
  const invalidar = useInvalidar()
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, fechaPautada }: { id: string; fechaPautada: string }) =>
      api<PublicacionDetalle>(
        `/api/publicaciones/${id}/reprogramar`,
        jsonInit('POST', { fechaPautada }),
      ),
    onMutate: async ({ id, fechaPautada }) => {
      await cliente.cancelQueries({ queryKey: ['grilla'] })
      const antes = cliente.getQueriesData<GrillaRespuesta>({ queryKey: ['grilla'] })
      cliente.setQueriesData<GrillaRespuesta>(
        { queryKey: ['grilla'] },
        (g) =>
          g && {
            ...g,
            publicaciones: g.publicaciones.map((p) =>
              p.id === id ? { ...p, fechaPautada } : p,
            ),
          },
      )
      return { antes }
    },
    onError: (_e, _v, ctx) => {
      for (const [clave, datos] of ctx?.antes ?? []) cliente.setQueryData(clave, datos)
    },
    onSettled: invalidar,
  })
}

export function useDarDeBaja() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (id: string) =>
      api<null>(`/api/publicaciones/${id}/baja`, jsonInit('POST')),
    onSuccess: invalidar,
  })
}

export function useRestaurar() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (id: string) =>
      api<null>(`/api/publicaciones/${id}/restaurar`, jsonInit('POST')),
    onSuccess: invalidar,
  })
}

/**
 * Si cada archivo pegado lo ve cualquiera con el enlace (ver
 * `esPublicoEnDrive`). `null` = no se pudo saber. Se pregunta una vez por
 * conjunto de archivos: lo que se pega no cambia de permisos a cada tecla.
 */
export function useDrivePublico(ids: string[]) {
  const api = useApi()
  const clave = [...ids].sort().join(',')
  return useQuery({
    queryKey: ['drive-publico', clave],
    queryFn: () =>
      api<Record<string, boolean | null>>(
        `/api/publicaciones/drive-publico?ids=${encodeURIComponent(clave)}`,
      ),
    enabled: ids.length > 0,
    staleTime: 60_000,
  })
}
